import { useState } from "react";
import type { MatchHistoryEntry } from "../game/api";
import type { PlayerHubState } from "../hooks/usePlayerHub";
import { BrandMark } from "./BrandMark";

interface PlayerHubScreenProps {
  data: PlayerHubState;
  playerId: string | null;
  onBack: () => void;
  onReplay: (match: MatchHistoryEntry) => void;
}

type HubView = "profile" | "history" | "leaderboard";

export function PlayerHubScreen({ data, playerId, onBack, onReplay }: PlayerHubScreenProps) {
  const [view, setView] = useState<HubView>("profile");
  return (
    <main className="hub-screen">
      <header className="hub-header">
        <button className="text-button" type="button" onClick={onBack}>← Home</button>
        <BrandMark compact />
        <span aria-hidden="true" />
      </header>
      <section className="hub-card" aria-labelledby="hub-title">
        <div className="hub-title-row">
          <div>
            <p className="eyebrow"><span /> Player center</p>
            <h1 id="hub-title">Your arena record</h1>
          </div>
          {data.profile && <div className="rating-chip"><small>Rating</small><strong>{data.profile.rating}</strong></div>}
        </div>
        <nav className="hub-nav" aria-label="Player center sections">
          {(["profile", "history", "leaderboard"] as const).map((candidate) => (
            <button key={candidate} type="button" aria-pressed={view === candidate} onClick={() => setView(candidate)}>
              {candidate === "profile" ? "Profile" : candidate === "history" ? "History" : "Leaderboard"}
            </button>
          ))}
        </nav>

        {data.status === "loading" && <div className="hub-loading" role="status"><span className="spinner" /> Loading verified player data…</div>}
        {data.status === "error" && (
          <div className="hub-empty" role="alert">
            <strong>Player data is unavailable</strong>
            <p>{data.error}</p>
            <button className="secondary-button" type="button" onClick={data.retry}>Try again</button>
          </div>
        )}
        {data.status === "ready" && view === "profile" && data.profile && (
          <div className="profile-view">
            <div className="profile-identity">
              <span className="player-avatar">{initial(data.profile.name)}</span>
              <div><small>Player</small><h2>{data.profile.name}</h2><p>{data.profile.league}</p></div>
            </div>
            <dl className="profile-stats">
              <div><dt>Matches</dt><dd>{data.profile.matchesPlayed}</dd></div>
              <div><dt>Wins</dt><dd>{data.profile.wins}</dd></div>
              <div><dt>Losses</dt><dd>{data.profile.losses}</dd></div>
              <div><dt>Win streak</dt><dd>{data.profile.winStreak}</dd></div>
              <div><dt>Goals for</dt><dd>{data.profile.goalsFor}</dd></div>
              <div><dt>Goals against</dt><dd>{data.profile.goalsAgainst}</dd></div>
            </dl>
          </div>
        )}
        {data.status === "ready" && view === "history" && (
          data.history.length === 0 ? (
            <div className="hub-empty"><strong>No completed matches yet</strong><p>Your authenticated match history will appear here.</p></div>
          ) : (
            <div className="record-list" aria-label="Match history">
              {data.history.map((match) => (
                <article key={match.matchId} className={`record-row record-row--${match.result}`}>
                  <span className="record-result">{match.result}</span>
                  <div><strong>{match.opponentName}</strong><small>{match.mode} · {formatDate(match.finishedAt)}</small></div>
                  <b>{match.scoreFor}<em>:</em>{match.scoreAgainst}</b>
                  <span className="record-delta">{match.ratingDelta === null ? "" : formatDelta(match.ratingDelta)}</span>
                  <button className="record-replay-button" type="button" onClick={() => onReplay(match)} aria-label={`Watch replay against ${match.opponentName}`}>
                    Replay <span aria-hidden="true">▶</span>
                  </button>
                </article>
              ))}
            </div>
          )
        )}
        {data.status === "ready" && view === "leaderboard" && (
          data.leaderboard.length === 0 ? (
            <div className="hub-empty"><strong>No season standings yet</strong><p>The leaderboard has no ranked players to show.</p></div>
          ) : (
            <div className="leaderboard-list" aria-label="Current season leaderboard">
              <div className="leaderboard-heading"><span>Rank</span><span>Player</span><span>W–L</span><span>Rating</span></div>
              {data.leaderboard.map((entry) => (
                <div key={entry.playerId} className={`leaderboard-row${entry.playerId === playerId ? " is-you" : ""}`}>
                  <b>#{entry.rank}</b><strong>{entry.name}{entry.playerId === playerId ? " · You" : ""}</strong>
                  <span>{entry.wins}–{entry.losses}</span><em>{entry.rating}</em>
                </div>
              ))}
            </div>
          )
        )}
      </section>
    </main>
  );
}

function initial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "P";
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDelta(value: number): string {
  return `${value > 0 ? "+" : ""}${value}`;
}
